/**
 * Changing or cancelling an event coming up at a place (place manager, unit
 * 6c), from its row under Next up. "Change" opens the schedule form (unit 6b,
 * in its change mode) filled in from the event as read by id; "Review" shows
 * one line of only what changes; "Apply" sends `admin_updateEvent` with the
 * event's id and only the changed fields. "Cancel" shows one line, and
 * "Cancel it" sends `cancelEvent`. Each has an undo for five minutes (6b's
 * window, {@link undoWindowOpen}): a change is sent back field by field; a
 * cancel is undone by making the same event again with `admin_createEvent`
 * (6b's input builder), as a new event (no "uncancel" exists). Staff on a dev
 * or preview build only, behind the same gate as scheduling
 * (`canScheduleEvents`); the admin console stays the full editor.
 *
 * Pure, like `schedule-event.ts` (whose draft, problems, input builder and
 * lines it reuses); the calls live in `schedule-event-api.ts`.
 *
 * What the backend does, and what follows here:
 *   - `admin_updateEvent` leaves out what is left out, but one field:
 *     `attachedProductIds` left out detaches every product, so the event's
 *     own are always sent back ({@link buildUpdateEventInput}).
 *   - its game change is record-keeping (the tables are set up at the start),
 *     and a game mode or player counts set for the old game would ride along,
 *     so a change of game clears those and says so (6b drops them from a
 *     template the same way). Scoring rules cannot be cleared through it, so
 *     an event with its own scoring changes game only in the admin console.
 *   - `cancelEvent` ends an event within a minute of its start instead of
 *     cancelling it, so Cancel is not offered then ({@link rowWriteOf}).
 */
import { durationLabel } from "@/features/places/schedule";
import { wasCancelled } from "@/features/places/last-events";
import { whenLabel, windowLabel } from "@/lib/dates";
import { plural } from "@/lib/utils";
import type { CreateEventInput, EventToChangeData, UpdateEventInput } from "@/data/schedule-event-api";
import { failureLine, undoWindowOpen, type WriteGuards } from "@/features/writes/writes";
import {
  END_INSTEAD_MS,
  SOONEST_START_MS,
  LostAnswerError,
  buildCreateEventInput,
  cancelOutcomeOf,
  fieldProblems,
  nameOf,
  problemLines,
  whoSeesItParts,
  rulesLabel,
  type DraftField,
  type ScheduleDraft,
  type ScheduleOptions,
  type ScheduleWriteApi,
  type SeasonOption,
  type UndoOutcome,
} from "@/features/places/schedule-event";

const MINUTE = 60 * 1000;

/** An event as read to change or cancel it (`EventToChangeQuery`). */
export type EventSnapshot = EventToChangeData;

/** What a Next up row offers at `now`: Change and Cancel (`open`); Change
 *  only, Cancel disabled (`too-close`: within a minute of its start, when
 *  `cancelEvent` would end it instead); nothing (`started`). */
export type RowWrite = "open" | "too-close" | "started";

export function rowWriteOf(startAt: number, now: number): RowWrite {
  if (startAt <= now) return "started";
  if (startAt - now <= END_INSTEAD_MS) return "too-close";
  return "open";
}

/** The quiet line a started event shows instead of buttons. */
export const STARTED_LINE = "Started events are run from the admin console.";

/** Why Cancel is disabled within a minute of the start. */
export const TOO_CLOSE_LINE = "Too close to start; end it from the room.";

/** Why the event as read cannot be changed or cancelled here; null when it can. */
export function snapshotProblem(event: EventSnapshot, now: number): string | null {
  if (wasCancelled(event)) return `${event.title} is cancelled already.`;
  if (event.startAt <= now) return STARTED_LINE;
  return null;
}

/** The form's fields as the event has them. A rules preset the event does not
 *  read back (no slug) is "" (said as not read, never sent). The run is the
 *  event's own, to the millisecond. */
export function draftOfEvent(event: EventSnapshot): ScheduleDraft {
  return {
    game: event.gatheringActivity.slug,
    startAt: event.startAt,
    minutes: (event.beginWindDownAt - event.startAt) / MINUTE,
    seasonId: event.season?.id ?? null,
    lobbyPreset: event.eventConfig?.lobbyGatheringSettingsTag?.slug ?? "",
    tablesPreset: event.gatheringSettings.slug ?? "",
    title: event.title,
    templateId: null,
  };
}

/** The rules presets the event does not read back: shown as not read, their
 *  pickers held (a field never read is never sent, nor could its undo send it back). */
export function unreadPresetsOf(event: EventSnapshot): Set<"lobbyPreset" | "tablesPreset"> {
  const draft = draftOfEvent(event);
  const unread = new Set<"lobbyPreset" | "tablesPreset">();
  if (!draft.lobbyPreset) unread.add("lobbyPreset");
  if (!draft.tablesPreset) unread.add("tablesPreset");
  return unread;
}

/** The form's games and presets with the event's own added where they are not
 *  offered (a game switched off, an archived preset), so the pickers show what
 *  the event has. */
export function changeOptionsOf(options: ScheduleOptions, event: EventSnapshot): ScheduleOptions {
  const add = (list: readonly { slug: string; name: string }[], slug: string | null | undefined, name: string | undefined) =>
    slug && name !== undefined && !list.some((o) => o.slug === slug) ? [...list, { slug, name }] : [...list];
  const lobby = event.eventConfig?.lobbyGatheringSettingsTag;
  const withTables = add(options.presets, event.gatheringSettings.slug, event.gatheringSettings.name);
  return {
    games: add(options.games, event.gatheringActivity.slug, event.gatheringActivity.name),
    presets: add(withTables, lobby?.slug, lobby?.name),
  };
}

/** The seasons offered with the event's own added when it is not among them
 *  (one ended or not listed); its dates unknown here, so it is not checked
 *  against the start (as for any season not offered). */
export function changeSeasonsOf(seasons: readonly SeasonOption[], event: EventSnapshot): SeasonOption[] {
  const own = event.season;
  if (!own || seasons.some((s) => s.id === own.id)) return [...seasons];
  return [...seasons, { id: own.id, title: own.title, startAt: -Infinity, endAt: Infinity }];
}

/** The fields the draft changes from the event's own. */
export function changedFields(before: ScheduleDraft, draft: ScheduleDraft): DraftField[] {
  const same = (a: number, b: number) => a === b || (Number.isNaN(a) && Number.isNaN(b));
  return ([
    ["game", draft.game !== before.game],
    ["startAt", !same(draft.startAt, before.startAt)],
    ["minutes", !same(draft.minutes, before.minutes)],
    ["seasonId", draft.seasonId !== before.seasonId],
    ["lobbyPreset", draft.lobbyPreset !== before.lobbyPreset],
    ["tablesPreset", draft.tablesPreset !== before.tablesPreset],
    ["title", draft.title.trim() !== before.title.trim()],
  ] as const)
    .filter(([, changed]) => changed)
    .map(([field]) => field);
}

/** How many scoring rules the event has of its own (`leaderboardScoringMetrics`). */
export function scoringRulesOf(event: EventSnapshot): number {
  return event.leaderboard?.scoringMetrics.length ?? 0;
}

/** What stops the change from being reviewed, in plain words; empty when
 *  nothing does. Only what changes is checked (a field left as it is is never
 *  in the way: an unread preset, a start already near); a start moved is
 *  checked against its season, and 6b's two minutes. */
export function changeProblems(
  event: EventSnapshot,
  draft: ScheduleDraft,
  now: number,
  seasons: readonly SeasonOption[] = [],
): string[] {
  // Started since the form opened: nothing more is sent from here.
  if (event.startAt <= now) return [STARTED_LINE];
  const changed = new Set(changedFields(draftOfEvent(event), draft));
  if (changed.size === 0) return ["Change something to review it."];
  const problems = problemLines(
    fieldProblems(draft, now, seasons).filter(([field]) => changed.has(field) || (field === "seasonId" && changed.has("startAt"))),
  );
  const scoring = scoringRulesOf(event);
  if (changed.has("game") && scoring > 0) {
    problems.unshift(`It has ${scoring} scoring ${plural(scoring, "rule")} of its own game; change its game from the admin console.`);
  }
  return problems;
}

/** The input's player-count settings, as 6b's game-specific ones (`GAME_SPECIFIC`). */
const PLAYER_COUNT_FIELDS = [
  "overrideMatchmakingPlayerCounts",
  "overrideMatchmakingMinPlayerCount",
  "overrideMatchmakingMaxPlayerCount",
  "overrideMatchmakingWaitTimeForPartialMatchMs",
  "overrideMatchmakingAbsoluteMaxWaitTimeMs",
] as const satisfies readonly (keyof UpdateEventInput & keyof EventSnapshot)[];

/** The event's attached products, by id: always sent back with a change
 *  (left out, `admin_updateEvent` detaches them all). */
export function productIdsOf(event: EventSnapshot): string[] {
  return event.attachedProducts.map((a) => a.product.id);
}

/** What a change of game clears: the event's game mode and its player-count
 *  settings, those it has (set for its old game). */
function gameSettingsCleared(event: EventSnapshot): Partial<UpdateEventInput> {
  const cleared: Partial<UpdateEventInput> = {};
  if (event.gatheringGameMode) cleared.gatheringGameModeId = null;
  for (const field of PLAYER_COUNT_FIELDS) {
    const value = event[field];
    if (value !== null && value !== false) cleared[field] = null;
  }
  return cleared;
}

/** The `admin_updateEvent` input for a change: the event's id and only what
 *  changes. Never the place (this place, fixed) and never an Intercom segment.
 *  Moving the start keeps the run (unless the run changes too) and, when the
 *  event was not yet to be shown by the new start, shows it now (or at the
 *  start, if sooner), as a new event is. Two fields ride along with what the
 *  user changed: a change of game clears the old game's game mode and player
 *  counts ({@link gameSettingsCleared}); the event's own products are always
 *  sent back ({@link productIdsOf}). */
export function buildUpdateEventInput(event: EventSnapshot, draft: ScheduleDraft, now: number): UpdateEventInput {
  const changed = new Set(changedFields(draftOfEvent(event), draft));
  const input: UpdateEventInput = { id: event.id };
  if (changed.has("title")) input.title = draft.title.trim();
  if (changed.has("game")) Object.assign(input, { gatheringActivitySlug: draft.game }, gameSettingsCleared(event));
  if (changed.has("tablesPreset")) input.gatheringSettingsTagSlug = draft.tablesPreset;
  if (changed.has("lobbyPreset")) input.lobbyGatheringSettingsTagSlug = draft.lobbyPreset;
  if (changed.has("seasonId")) input.seasonId = draft.seasonId;
  if (changed.has("startAt") || changed.has("minutes")) {
    const run = changed.has("minutes") ? draft.minutes * MINUTE : event.beginWindDownAt - event.startAt;
    if (changed.has("startAt")) input.startAt = draft.startAt;
    const windDown = draft.startAt + run;
    if (windDown !== event.beginWindDownAt) input.beginWindDownAt = windDown;
    if (event.revealAt > draft.startAt) input.revealAt = Math.min(Math.floor(now / MINUTE) * MINUTE, draft.startAt);
  }
  input.attachedProductIds = productIdsOf(event);
  return input;
}

/** Each field a change may send, and the event's value for it before: what
 *  its undo sends back. A preset sent was read (an unread one is never sent). */
const BEFORE: { [K in Exclude<keyof UpdateEventInput, "id">]?: (event: EventSnapshot) => UpdateEventInput[K] } = {
  title: (e) => e.title,
  gatheringActivitySlug: (e) => e.gatheringActivity.slug,
  gatheringSettingsTagSlug: (e) => e.gatheringSettings.slug,
  lobbyGatheringSettingsTagSlug: (e) => e.eventConfig?.lobbyGatheringSettingsTag?.slug,
  seasonId: (e) => e.season?.id ?? null,
  startAt: (e) => e.startAt,
  beginWindDownAt: (e) => e.beginWindDownAt,
  revealAt: (e) => e.revealAt,
  gatheringGameModeId: (e) => e.gatheringGameMode?.id ?? null,
  overrideMatchmakingPlayerCounts: (e) => e.overrideMatchmakingPlayerCounts,
  overrideMatchmakingMinPlayerCount: (e) => e.overrideMatchmakingMinPlayerCount,
  overrideMatchmakingMaxPlayerCount: (e) => e.overrideMatchmakingMaxPlayerCount,
  overrideMatchmakingWaitTimeForPartialMatchMs: (e) => e.overrideMatchmakingWaitTimeForPartialMatchMs,
  overrideMatchmakingAbsoluteMaxWaitTimeMs: (e) => e.overrideMatchmakingAbsoluteMaxWaitTimeMs,
  attachedProductIds: productIdsOf,
};

/** The undo of a change: the same id and exactly the fields `sent`, each with
 *  the event's value before the change. */
export function undoChangeInputOf(event: EventSnapshot, sent: UpdateEventInput): UpdateEventInput {
  const back: Record<string, unknown> = { id: event.id };
  for (const key of Object.keys(sent) as (keyof UpdateEventInput)[]) {
    if (key === "id") continue;
    const before = BEFORE[key];
    if (!before) throw new Error(`No earlier value kept for ${key}.`);
    back[key] = before(event);
  }
  return back as UpdateEventInput;
}

/** What the review line names besides the input. */
export interface ChangeReviewContext {
  options: ScheduleOptions;
  seasons: readonly SeasonOption[];
  /** For tests; the reader's own otherwise. */
  timeZone?: string;
  locale?: string;
  now?: number;
}

/** What the event carries that changes who sees it, as 6b's review line names it. */
function carriesOf(event: EventSnapshot): string[] {
  return whoSeesItParts({ visibility: event.visibility, guests: event.guestList.totalCount, featured: event.featured });
}

/** The one line "Review" shows for a change: only what changes ("Moves to
 *  Tue, Oct 7, 7:00 – 8:00 PM PDT · game: Hearts → Spades"), then what rides
 *  along (the old game's settings cleared, the products kept, the event
 *  shown now), then what the event carries that changes who sees it. */
export function changeReviewLine(event: EventSnapshot, input: UpdateEventInput, ctx: ChangeReviewContext): string {
  const at = { timeZone: ctx.timeZone, locale: ctx.locale };
  const start = input.startAt ?? event.startAt;
  const end = input.beginWindDownAt ?? event.beginWindDownAt;
  const presetsAfter = {
    gatheringSettingsTagSlug: input.gatheringSettingsTagSlug ?? event.gatheringSettings.slug ?? "",
    lobbyGatheringSettingsTagSlug: input.lobbyGatheringSettingsTagSlug ?? event.eventConfig?.lobbyGatheringSettingsTag?.slug ?? "",
  };
  const presetsBefore = {
    gatheringSettingsTagSlug: event.gatheringSettings.slug ?? "",
    lobbyGatheringSettingsTagSlug: event.eventConfig?.lobbyGatheringSettingsTag?.slug ?? "",
  };
  const seasonTitle = (id: string | null | undefined) =>
    id ? (ctx.seasons.find((s) => s.id === id)?.title ?? (event.season?.id === id ? event.season.title : id)) : null;
  const cleared = [
    input.gatheringGameModeId !== undefined && "game mode",
    PLAYER_COUNT_FIELDS.some((f) => input[f] !== undefined) && "player counts",
  ].filter((p): p is string => !!p);
  const products = input.attachedProductIds?.length ?? 0;
  const newSeason = seasonTitle(input.seasonId);
  // A preset not read back ("") is said as such, never as a blank name.
  const presets = [...ctx.options.presets, { slug: "", name: "not read" }];
  return [
    input.startAt !== undefined
      ? `Moves to ${windowLabel(start, end, at)}`
      : input.beginWindDownAt !== undefined &&
        `Runs ${windowLabel(start, end, at)}, ${durationLabel(end - start)} (was ${durationLabel(event.beginWindDownAt - event.startAt)})`,
    input.revealAt !== undefined && "shown to players from now",
    input.gatheringActivitySlug !== undefined &&
      `game: ${event.gatheringActivity.name} → ${nameOf(ctx.options.games, input.gatheringActivitySlug ?? "")}`,
    (input.gatheringSettingsTagSlug !== undefined || input.lobbyGatheringSettingsTagSlug !== undefined) &&
      `rules: ${rulesLabel(presetsBefore, presets)} → ${rulesLabel(presetsAfter, presets)}`,
    input.seasonId !== undefined &&
      (newSeason
        ? `season: ${seasonTitle(event.season?.id) ?? "none"} → ${newSeason}`
        : `season: ${seasonTitle(event.season?.id)} → none, so it will not show under Next up`),
    input.title !== undefined && `title: ${event.title} → ${input.title}`,
    cleared.length > 0 && `its ${cleared.join(" and ")} cleared (another game)`,
    products > 0 && (products === 1 ? "its product stays attached" : `its ${products} products stay attached`),
    ...carriesOf(event),
  ]
    .filter((part): part is string => !!part)
    .join(" · ");
}

/** Fields of an event `admin_createEvent` takes and the read has, carried
 *  over when it is made again; nulls left out (the builder's default stands). */
function carriedOf(event: EventSnapshot): Partial<CreateEventInput> {
  const guests = event.guestList.nodes.map((u) => u.public.username);
  const tile = event.tileBackground;
  // Nulls included here (the read's), then left out below.
  const carried: { [K in keyof CreateEventInput]?: CreateEventInput[K] | null } = {
    description: event.description,
    visibility: event.visibility,
    featured: event.featured,
    gatheringCount: event.gatheringCount,
    showFinalResultsDuration: event.showFinalResultsDuration,
    hasCountdownState: event.hasCountdownState,
    countdownTime: event.countdownTime,
    matchmakingEnabled: event.matchmakingEnabled,
    matchmakingWaitingText: event.matchmakingWaitingText,
    subgatheringViewMode: event.subgatheringViewMode,
    deepLinkToSubgatheringsEnabled: event.deepLinkToSubgatheringsEnabled,
    howItWorksIntercomArticleId: event.howItWorksIntercomArticleId,
    tileBackgroundCloudPath: event.tileBackgroundCloudPath,
    tileBackground: tile && (tile.skinnyImageUrl || tile.wideImageUrl) ? tile : null,
    overrideMatchmakingPlayerCounts: event.overrideMatchmakingPlayerCounts,
    overrideMatchmakingMinPlayerCount: event.overrideMatchmakingMinPlayerCount,
    overrideMatchmakingMaxPlayerCount: event.overrideMatchmakingMaxPlayerCount,
    overrideMatchmakingWaitTimeForPartialMatchMs: event.overrideMatchmakingWaitTimeForPartialMatchMs,
    overrideMatchmakingAbsoluteMaxWaitTimeMs: event.overrideMatchmakingAbsoluteMaxWaitTimeMs,
    gatheringGameModeId: event.gatheringGameMode?.id,
    flatPrizingConfigurationId: event.flatPrizingConfiguration?.id,
    eventTemplateId: event.eventTemplate?.id,
    attachedProductIds: productIdsOf(event),
    guestListByUsernames: guests.length > 0 ? guests.join(" ") : null,
    leaderboardTitle: event.leaderboard?.title,
  };
  return Object.fromEntries(Object.entries(carried).filter(([, v]) => v !== null && v !== undefined)) as Partial<CreateEventInput>;
}

/** Why the event cannot be made again from here (so Cancel offers no undo);
 *  null when it can. A preset not read back, or a guest list longer than
 *  the read, could not be sent as it was. */
export function remakeProblem(event: EventSnapshot): string | null {
  if (unreadPresetsOf(event).size > 0) return "its rules did not read";
  if (event.guestList.totalCount > event.guestList.nodes.length) {
    return `its guest list is longer than the ${event.guestList.nodes.length} read here`;
  }
  return null;
}

/** The `admin_createEvent` input that makes a cancelled event again: 6b's
 *  builder, with the event's own fields carried and its form fields as the
 *  draft, hosted by its host, Intercom segment none (the builder's rule).
 *  Then the event's own: its place (its own, or none, so it is at its season's
 *  place again), its exact times, and its reveal when that is still to come.
 *  Not carried: its scoring rules (their names do not read back) and its
 *  followers ({@link cancelReviewLine} says so). */
export function remakeInputOf(event: EventSnapshot, now: number): CreateEventInput {
  const input = buildCreateEventInput(draftOfEvent(event), {
    placeId: event.ownPlace?.id ?? "",
    username: event.host.public.username,
    now,
    template: null,
    carried: carriedOf(event),
  });
  // No place of its own: none sent, so it is at its season's place again.
  const { placeId, ...rest } = input;
  return {
    ...rest,
    ...(placeId ? { placeId } : {}),
    beginWindDownAt: event.beginWindDownAt,
    ...(event.revealAt > now ? { revealAt: event.revealAt } : {}),
  };
}

/** The one line "Cancel" shows: "Cancels Hearts at The Parlor, Tue, Oct 7,
 *  7:00 PM PDT. Players who follow the season will not see it. Undo makes it
 *  again with the same settings; a new event id.", with what the event
 *  carries, what the undo cannot carry, or why it offers none. */
export function cancelReviewLine(event: EventSnapshot, ctx: { timeZone?: string; locale?: string; now?: number } = {}): string {
  const when = whenLabel(event.startAt, { zoned: true, timeZone: ctx.timeZone, locale: ctx.locale, now: ctx.now });
  const scoring = scoringRulesOf(event);
  const followers = event.followerCount;
  const notCarried = [
    scoring > 0 && `its ${scoring} scoring ${plural(scoring, "rule")}`,
    followers > 0 && `its ${followers} ${plural(followers, "follower")}`,
  ].filter((p): p is string => !!p);
  const carries = carriesOf(event);
  const problem = remakeProblem(event);
  return [
    `Cancels ${event.title}, ${when}.`,
    event.season && "Players who follow the season will not see it.",
    problem
      ? `There is no undo: ${problem}.`
      : `Undo makes it again with the same settings${carries.length > 0 ? ` (${carries.join(", ")})` : ""}; a new event id.`,
    !problem && notCarried.length > 0 && `Not made again: ${notCarried.join(" and ")}.`,
  ]
    .filter((part): part is string => !!part)
    .join(" ");
}

/** An event changed or cancelled here, and where its undo stands: ready;
 *  going; done (`undone`, with the new id for a cancel made again); failed
 *  (Undo stays: sending a change back again is safe); or lost (a cancel's undo
 *  whose answer never came: it may have made the event again, so no Undo
 *  again). A cancel the backend ended instead has no undo (`ended`). */
export interface ChangedEvent {
  kind: "changed" | "cancelled";
  id: string;
  title: string;
  startAt: number;
  /** When it was done (ms since epoch): "Undo" lasts five minutes from then. */
  madeAt: number;
  /** The event as read before (what its undo restores). */
  before: EventSnapshot;
  /** A change: what was sent. */
  sent: UpdateEventInput | null;
  /** A cancel: how the backend did it (an end has no undo). */
  outcome: UndoOutcome | null;
  undo: "ready" | "undoing" | { undone: string | null } | { failed: string } | { lost: string };
}

/** Whether "Undo" shows for `changed` at `now`: within the five minutes,
 *  ready (or failed), and, for a cancel, cancelled (not ended) and makeable again. */
export function canUndoChange(changed: ChangedEvent, now: number): boolean {
  return undoable(changed) && undoWindowOpen(changed, now) && undoChangeProblem(changed, now) === null;
}

/** Whether `changed` has an undo at all, time aside: ready (or failed: it
 *  may be pressed again), and, for a cancel, cancelled (not ended) and
 *  makeable again. */
export function undoable(changed: ChangedEvent): boolean {
  const ready = changed.undo === "ready" || (typeof changed.undo === "object" && "failed" in changed.undo);
  return ready && (changed.kind === "changed" || (changed.outcome === "cancelled" && remakeProblem(changed.before) === null));
}

/** Whether `changed` has been undone. */
export function changeUndone(changed: ChangedEvent): boolean {
  return typeof changed.undo === "object" && "undone" in changed.undo;
}

/** Why the undo cannot go now, in plain words; null when it can. A change
 *  whose start moved back to under two minutes away, or whose event has
 *  started; a cancel whose event would start within two minutes. */
export function undoChangeProblem(changed: ChangedEvent, now: number): string | null {
  if (!undoWindowOpen(changed, now)) return "The five minutes to undo have passed. Use the admin console.";
  if (changed.kind === "cancelled") {
    return changed.before.startAt < now + SOONEST_START_MS
      ? `${changed.title} would start in under 2 minutes, so it is not made again here. Use the admin console.`
      : null;
  }
  if (changed.startAt <= now) return STARTED_LINE;
  const back = changed.sent?.startAt !== undefined ? changed.before.startAt : null;
  if (back !== null && back < now + SOONEST_START_MS) {
    return `Its old start is under 2 minutes away, so it is not moved back here. Use the admin console.`;
  }
  return null;
}

/** The line under Next up for an event changed or cancelled here: what was
 *  done (and, for a cancel, why it left the list), then how its undo went. */
export function changedLine(changed: ChangedEvent): string {
  const undo = changed.undo;
  if (typeof undo === "object" && "undone" in undo) {
    return changed.kind === "changed"
      ? `Undone: ${changed.title} is as it was.`
      : `Undone: ${changed.title} is made again, as a new event.`;
  }
  if (typeof undo === "object" && "lost" in undo) return undo.lost;
  if (changed.kind === "changed") return `Changed: ${changed.title}.`;
  if (changed.outcome === "ended") return `${changed.title} was about to start, so pop.in ended it instead of cancelling it.`;
  if (changed.outcome === "either") {
    return `${changed.title} was about to start, so pop.in cancelled it or, if it had reached its last minute, ended it.`;
  }
  return `Cancelled: ${changed.title}, ${whenLabel(changed.startAt, { zoned: true })}.`;
}

/** What a change whose answer never came says (no Apply again from that form). */
export const CHANGE_LOST_LINE =
  "Could not hear back from pop.in, so the change may have been made. Next up is read again in a few seconds and in half a minute: check it then, or the admin console, before changing it again.";

/** What a cancel whose answer never came says (no Cancel it again from that review). */
export const CANCEL_LOST_LINE =
  "Could not hear back from pop.in, so it may have been cancelled. Next up is read again in a few seconds and in half a minute: check it then, or the admin console, before cancelling it again.";

/** What a cancel's undo whose answer never came says (no Undo again: it may have made the event). */
export function remakeLostLine(title: string): string {
  return `Could not hear back from pop.in, so ${title} may have been made again. Next up is read again in a few seconds and in half a minute: check it then, or the admin console, before making it again.`;
}

/** Change it (`input` as {@link buildUpdateEventInput} makes it from
 *  `before`); the change made, stamped at the answer, its undo ready. A
 *  refusal throws its plain line; a lost answer reads Next up again and throws
 *  a `LostAnswerError` (that form offers no Apply again). */
export async function changeEvent(
  before: EventSnapshot,
  input: UpdateEventInput,
  api: Pick<ScheduleWriteApi, "update">,
  guards: WriteGuards,
  clock: () => number = Date.now,
): Promise<ChangedEvent> {
  try {
    await api.update(input);
  } catch (e) {
    console.warn("Could not change the event:", e);
    if (!guards.isLost(e)) throw new Error(failureLine(e, "change it"));
    guards.reread();
    throw new LostAnswerError(true, CHANGE_LOST_LINE);
  }
  return {
    kind: "changed",
    id: before.id,
    title: input.title ?? before.title,
    startAt: input.startAt ?? before.startAt,
    madeAt: clock(),
    before,
    sent: input,
    outcome: null,
    undo: "ready",
  };
}

/** Cancel it; the cancel made, with how the backend did it. Checked again at
 *  the press, whatever is on screen: never once it has started, nor within
 *  its last minute (the backend would end it). Failures as {@link changeEvent}. */
export async function cancelComingEvent(
  before: EventSnapshot,
  api: Pick<ScheduleWriteApi, "cancel">,
  guards: WriteGuards,
  clock: () => number = Date.now,
): Promise<ChangedEvent> {
  const sent = clock();
  const write = rowWriteOf(before.startAt, sent);
  if (write !== "open") throw new Error(write === "started" ? STARTED_LINE : TOO_CLOSE_LINE);
  try {
    await api.cancel(before.id);
  } catch (e) {
    console.warn("Could not cancel the event:", e);
    if (!guards.isLost(e)) throw new Error(failureLine(e, "cancel it"));
    guards.reread();
    throw new LostAnswerError(true, CANCEL_LOST_LINE);
  }
  const answered = clock();
  return {
    kind: "cancelled",
    id: before.id,
    title: before.title,
    startAt: before.startAt,
    madeAt: answered,
    before,
    sent: null,
    outcome: cancelOutcomeOf(before.startAt, sent, answered),
    undo: "ready",
  };
}

/** Undo a change or a cancel; how it went, as `ChangedEvent["undo"]` (never
 *  throws). A change: its fields sent back ({@link undoChangeInputOf}); a
 *  failure keeps Undo (sending them again is safe). A cancel: the event made
 *  again ({@link remakeInputOf}); a lost answer reads Next up again and offers
 *  no Undo again (a second could make a second event). Refused (as a failure,
 *  in plain words) when {@link undoChangeProblem} says so. */
export async function undoChangedEvent(
  changed: ChangedEvent,
  api: Pick<ScheduleWriteApi, "update" | "create">,
  guards: WriteGuards,
  now: number,
): Promise<ChangedEvent["undo"]> {
  const problem = undoChangeProblem(changed, now);
  if (problem) return { failed: problem };
  if (changed.kind === "changed") {
    try {
      await api.update(undoChangeInputOf(changed.before, changed.sent ?? { id: changed.id }));
      return { undone: null };
    } catch (e) {
      console.warn("Could not undo the change:", e);
      return { failed: failureLine(e, "undo it") };
    }
  }
  try {
    const made = await api.create(remakeInputOf(changed.before, now));
    return { undone: made.id };
  } catch (e) {
    console.warn("Could not make the cancelled event again:", e);
    if (!guards.isLost(e)) return { failed: failureLine(e, "make it again") };
    guards.reread();
    return { lost: remakeLostLine(changed.title) };
  }
}
